import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import RoutePath from './RoutePath'

dayjs.extend(customParseFormat)

/**
 * Generate a URL-friendly slug from a text string
 * Handles special characters, spaces, and Scandinavian letters
 */
export const generateSeoSlug = (name: string): string => {
  if (!name) return ''

  return (
    name
      .toLowerCase()
      .trim()
      // Replace Scandinavian characters
      .replace(/[åä]/g, 'a')
      .replace(/[ö]/g, 'o')
      .replace(/[æ]/g, 'ae')
      .replace(/[ø]/g, 'o')
      // Remove all non-alphanumeric characters except spaces and hyphens
      .replace(/[^a-z0-9\s-]/g, '')
      // Replace spaces with hyphens
      .replace(/\s+/g, '-')
      // Replace multiple hyphens with single hyphen
      .replace(/-+/g, '-')
      // Remove leading/trailing hyphens
      .replace(/^-|-$/g, '')
  )
}

// Locale-independent English weekday names indexed by dayjs .day() (0 = Sunday)
const ENGLISH_WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

/**
 * Returns the full English weekday name (lowercase) for an ISO date string.
 * Date-only strings (e.g. "2025-09-01") are parsed strictly as local calendar days.
 * Returns null for falsy or invalid input.
 *
 * Uses a numeric day-of-week index rather than locale-sensitive formatting so that
 * the result is always English regardless of any globally-set dayjs locale (e.g. 'sv').
 *
 * @example weekdayFromIsoDate("2025-09-01") // "monday"
 */
export const weekdayFromIsoDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null

  const trimmed = dateStr.trim()
  if (!trimmed) return null

  const parsed = trimmed.includes('T') ? dayjs(trimmed) : dayjs(trimmed, 'YYYY-MM-DD', true)
  if (!parsed.isValid()) return null

  return ENGLISH_WEEKDAYS[parsed.day()] ?? null
}

/**
 * Generate a URL-friendly slug that combines a period name with an activity name.
 * Used for new activities to produce season-scoped slugs like "ht-2025-salsa-beginner".
 * Falls back to a plain name-only slug when no period is provided.
 */
export const generatePeriodBasedSlug = (name: string, period: string | undefined): string => {
  const nameSlug = generateSeoSlug(name)
  if (!period) return nameSlug
  const periodSlug = generateSeoSlug(period)
  return [periodSlug, nameSlug].filter(Boolean).join('-')
}

/**
 * Validates that a URL path is in the correct format
 */
export function isValidActivityUrlPath(path: string): boolean {
  // Only lowercase letters, numbers, and hyphens
  // Must start and end with alphanumeric
  return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(path)
}

/**
 * Extracts subdomain from hostname
 * "danceschool.coursely.se" → "danceschool"
 * "courses.danceschool.se" → null (custom domain)
 */
export function extractCourselySubdomain(hostname: string): string | null {
  const match = hostname.match(/^([^.]+)\.coursely\.se$/)
  return match ? match[1] : null
}

/**
 * Checks if hostname is a Coursely subdomain
 */
export function isCourselySubdomain(hostname: string): boolean {
  return hostname.endsWith('.coursely.se') && hostname.split('.').length === 3
}

/**
 * Gets the base path for activities (e.g., "/activity/") derived from RoutePath.ACTIVITY_DETAIL
 * This ensures we have a single source of truth for the activity path prefix
 */
export function getActivityBasePath(): string {
  // RoutePath.ACTIVITY_DETAIL = '/activity/:activityId' → '/activity/'
  return RoutePath.ACTIVITY_DETAIL.split(':')[0]
}

/**
 * Extracts the activity ID from a request path
 * @param requestPath - The full request path (e.g., "/activity/abc123")
 * @returns The activity ID (e.g., "abc123") or null if not an activity path
 */
export function extractActivityIdFromRequest(requestPath: string): string | null {
  const basePath = getActivityBasePath()
  if (!requestPath.startsWith(basePath)) {
    return null
  }
  const activityId = requestPath.replace(basePath, '').split('/')[0]
  return activityId || null
}

/**
 * Checks if a request path is an activity path
 */
export function isActivityPath(requestPath: string): boolean {
  return requestPath.startsWith(getActivityBasePath())
}

/**
 * Builds a full URL for an activity page using RoutePath.ACTIVITY_DETAIL
 * @param hostname - The hostname (e.g., "danceschool.coursely.se")
 * @param activityId - The activity ID (e.g., "abc123")
 * @returns Full URL (e.g., "https://danceschool.coursely.se/activity/abc123")
 */
export function buildActivityUrl(hostname: string, activityId: string): string {
  const path = RoutePath.ACTIVITY_DETAIL.replace(':activityId', activityId)
  return `https://${hostname}${path}`
}
